import {Button} from '@wandb/components';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import React, {useCallback, useMemo, useState} from 'react';

import {isMobile} from '../../../../util/panelbankFlow';
import {PanelBankFlowSectionConfig} from '../../types';
import {
  getFlowConfigMaxRowsPerPage,
  getFlowConfigPageSizeOptions,
  PageSizeOption,
} from '../panelBankFlowPaginationUtils';

type PanelBankSectionPaginationMenuProps = {
  flowConfig: PanelBankFlowSectionConfig;
  panelsPerPage: number;
  startPanelIndex: number;
  updateFlowConfig: (newFlowConfig: PanelBankFlowSectionConfig) => void;
  numTotalPanelsInSection: number;
};

export const PanelBankSectionPaginationMenu = ({
  flowConfig,
  panelsPerPage,
  startPanelIndex,
  updateFlowConfig,
  numTotalPanelsInSection,
}: PanelBankSectionPaginationMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const buttonText = `${(startPanelIndex + 1).toLocaleString()}-${Math.min(
    startPanelIndex + panelsPerPage,
    numTotalPanelsInSection
  ).toLocaleString()} of ${numTotalPanelsInSection.toLocaleString()}`;

  return (
    <DropdownMenu.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          isActive={isMenuOpen}
          data-test="panel-bank-section-pagination-count"
          onPress={() => {
            setIsMenuOpen(!isMenuOpen);
          }}
          variant="ghost">
          {buttonText}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          // We're specifying z-index so menu shows up on single run page.
          className="z-[1] shadow-md shadow-oblivion/[0.16] dark:shadow-oblivion/[0.48]"
          align="start"
          onCloseAutoFocus={e => e.preventDefault()}>
          <PageSizeOptions
            flowConfig={flowConfig}
            panelsPerPage={panelsPerPage}
            updateFlowConfig={updateFlowConfig}
            numTotalPanelsInSection={numTotalPanelsInSection}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

const PageSizeOptions = ({
  flowConfig,
  numTotalPanelsInSection,
  panelsPerPage,
  updateFlowConfig,
}: {
  flowConfig: PanelBankFlowSectionConfig;
  numTotalPanelsInSection: number;
  panelsPerPage: number;
  updateFlowConfig: (newFlowConfig: PanelBankFlowSectionConfig) => void;
}) => {
  const options = useMemo(
    () =>
      getFlowConfigPageSizeOptions({
        columnsPerPage: flowConfig.columnsPerPage,
        numTotalPanelsInSection,
      }),
    [flowConfig.columnsPerPage, numTotalPanelsInSection]
  );

  const onSelect = useCallback(
    (option: PageSizeOption) => {
      if (isMobile()) {
        // Mobile pagination differs from desktop: mobile is fixed at 1 column, so pagination
        // controls panels per page directly. Desktop allows multiple columns, so pagination
        // controls rows per page (panels per page = rows × columns).
        updateFlowConfig({
          ...flowConfig,
          mobileRowsPerPage:
            option === 'all' ? numTotalPanelsInSection : option,
        });
      } else {
        updateFlowConfig({
          ...flowConfig,
          rowsPerPage: getFlowConfigMaxRowsPerPage({
            columnsPerPage: flowConfig.columnsPerPage,
            numTotalPanelsInSection,
            option,
          }),
        });
      }
    },
    [flowConfig, numTotalPanelsInSection, updateFlowConfig]
  );

  return (
    <>
      {options.map((option: PageSizeOption) => {
        return (
          <DropdownMenu.CheckboxItem
            key={`panel-bank-section-pagination-option-${option}`}
            checked={
              option === 'all'
                ? panelsPerPage >= numTotalPanelsInSection
                : option === panelsPerPage
            }
            className="flex justify-between text-moon-800 dark:text-moon-300"
            onSelect={() => onSelect(option)}>
            <div className="flex w-full items-center justify-between gap-16">
              <span>Show {option} panels</span>
            </div>
            <DropdownMenu.ItemIndicator>
              <Icon name="checkmark" width={18} height={18} />
            </DropdownMenu.ItemIndicator>
          </DropdownMenu.CheckboxItem>
        );
      })}
    </>
  );
};
